/*
 * Looks for card-like surfaces (card tiles, board tiles, previews). `lookProps()` sets
 * `data-look` and the custom properties; nothing here depends on user-provided strings.
 * System states (labels strip, deadline pills, checklist/pin rail, completed fade) are separate
 * elements and keep their own colors: a look only paints the surface.
 */
.surface[data-look='soft'] {
  background: color-mix(in srgb, var(--look-color, transparent) 16%, var(--color-surface));
}

.surface[data-look='border'] {
  box-shadow: inset 0 0 0 2px var(--look-border, var(--look-color));
}

.surface[data-look='accent'] {
  box-shadow: inset 0 4px 0 var(--look-border, var(--look-color));
}

.surface[data-look='header'] {
  background:
    linear-gradient(
      color-mix(in srgb, var(--look-header, var(--look-color)) 34%, transparent) 0 2.1rem,
      transparent 2.1rem
    ),
    var(--color-surface);
}

.surface[data-look='solid'],
.surface[data-look='gradient'] {
  --color-text: var(--look-fg);
  --color-text-muted: color-mix(in srgb, var(--look-fg) 78%, transparent);
  /* The completion ring, pin and links are drawn in the accent color: keep them visible. */
  --color-accent: var(--look-fg);
  color: var(--look-fg);
}

.surface[data-look='solid'] {
  background: var(--look-body, var(--look-color));
}

.surface[data-look='gradient'] {
  background: var(--look-gradient);
}

.surface[data-look='pattern'] {
  background-color: var(--look-pattern-bg, var(--color-surface));
  background-image: var(--look-pattern);
  background-size: var(--look-pattern-size);
  --color-text: var(--look-fg, var(--color-text));
}

/*
 * Advanced (per-element) colors apply on top of any style except the full-surface ones: the
 * body tints the surface, the border draws the frame, the header tints the top strip.
 * A readable text color is derived from the body color by lookProps().
 */
.surface[data-has-body]:not([data-look='gradient']):not([data-look='pattern']) {
  background-color: var(--look-body);
  --color-text: var(--look-fg);
  --color-text-muted: color-mix(in srgb, var(--look-fg) 78%, transparent);
  color: var(--look-fg);
}

.surface[data-has-border] {
  box-shadow: inset 0 0 0 2px var(--look-border);
}

.surface[data-has-header]:not([data-look='gradient']):not([data-look='pattern']) {
  background-image: linear-gradient(
    color-mix(in srgb, var(--look-header) 40%, transparent) 0 2.1rem,
    transparent 2.1rem
  );
}

/*
 * Explicit text colors (after every fill rule, so they win). `--card-title-color` is read by the
 * card title; the content panel (`data-look-panel`) is a readable backing behind the text, mainly
 * for gradients and patterns.
 */
.surface[data-has-text] {
  --color-text: var(--look-text);

  color: var(--look-text);
}

.surface[data-has-muted] {
  --color-text-muted: var(--look-muted);
}

.surface[data-has-title] {
  --card-title-color: var(--look-title);
}

.surface[data-has-surface] [data-look-panel] {
  --color-text: var(--look-surface-fg);
  --color-text-muted: color-mix(in srgb, var(--look-surface-fg) 78%, transparent);
  --color-accent: var(--look-surface-fg);

  padding: var(--space-1) var(--space-2);
  border-radius: var(--radius-sm);
  background: var(--look-surface);
  color: var(--look-surface-fg);
}

.surface[data-has-surface][data-has-text] [data-look-panel] {
  --color-text: var(--look-text);

  color: var(--look-text);
}

.surface[data-has-surface][data-has-muted] [data-look-panel] {
  --color-text-muted: var(--look-muted);
}
